import { useEffect, useState } from "react";
import { api, inr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";
import { Wand2 } from "lucide-react";

export default function IncomeTax() {
  const [form, setForm] = useState({
    gross_income: 1500000,
    deductions_80c: 150000,
    deductions_80d: 25000,
    home_loan_interest: 0,
    nps_80ccd1b: 50000,
    standard_deduction: 75000,
    other_deductions: 0,
    age_group: "below_60",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ytd, setYtd] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/dashboard/summary");
        setYtd(r.data?.taxable_income_ytd || 0);
      } catch {}
    })();
  }, []);

  const useYtd = () => {
    if (ytd && ytd > 0) {
      setForm((f) => ({ ...f, gross_income: Math.round(ytd) }));
      toast.success(`Pulled your YTD taxable income: ${inr(ytd)}`);
    } else {
      toast.message("No income recorded yet — log invoices or other income first.");
    }
  };

  const calculate = async () => {
    setLoading(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, k === "age_group" ? v : Number(v) || 0])
      );
      const r = await api.post("/tax/income-tax", payload);
      setResult(r.data);
    } finally { setLoading(false); }
  };

  const update = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div className="space-y-8" data-testid="tax-page">
      <div>
        <p className="tc-overline mb-2">Income tax · FY 2025–26</p>
        <h1 className="font-['Outfit'] text-3xl md:text-4xl font-bold tracking-tight">Old vs New, side-by-side</h1>
        <p className="text-sm text-[#5a5a53] mt-2 max-w-xl">
          Punch in your numbers. We'll compute liability under both regimes, recommend the cheaper one,
          and lay out your advance-tax schedule.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Inputs */}
        <div className="tc-card p-6 lg:col-span-1 space-y-4">
          <div className="flex items-center justify-between">
            <p className="tc-overline">Inputs</p>
            <button onClick={useYtd} data-testid="tax-use-ytd"
              className="flex items-center gap-1 text-xs text-[#d95d39] hover:underline">
              <Wand2 size={12} /> Use YTD income
            </button>
          </div>
          {ytd > 0 && (
            <div className="text-[11px] text-[#5a5a53] -mt-2 font-mono">
              Your YTD taxable income: <span className="font-bold text-[#0a0a0a]">{inr(ytd)}</span>
            </div>
          )}
          {[
            ["gross_income", "Gross income (₹)"],
            ["deductions_80c", "80C investments"],
            ["deductions_80d", "80D health insurance"],
            ["home_loan_interest", "Home loan interest"],
            ["nps_80ccd1b", "NPS (80CCD-1B)"],
            ["other_deductions", "Other deductions"],
            ["standard_deduction", "Standard deduction (new regime)"],
          ].map(([k, label]) => (
            <div key={k}>
              <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">{label}</Label>
              <Input type="number" value={form[k]} onChange={(e) => update(k, e.target.value)}
                className="mt-2 h-11 rounded-md border-[#e5e5e0]" data-testid={`tax-input-${k}`} />
            </div>
          ))}
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Age group</Label>
            <Select value={form.age_group} onValueChange={(v) => update("age_group", v)}>
              <SelectTrigger data-testid="tax-age-select" className="mt-2 h-11 rounded-md border-[#e5e5e0]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="below_60">Below 60</SelectItem>
                <SelectItem value="60_to_80">Senior (60–80)</SelectItem>
                <SelectItem value="above_80">Super senior (80+)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={calculate} disabled={loading} data-testid="tax-calc-btn"
            className="w-full bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white rounded-md h-11">
            {loading ? "Calculating..." : "Compare regimes"}
          </Button>
        </div>

        {/* Results */}
        <div className="lg:col-span-2 space-y-6">
          {result ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <RegimeCard
                  title="Old Regime"
                  taxable={result.old_regime.taxable_income}
                  tax={result.old_regime.tax}
                  recommended={result.recommended_regime === "old"}
                  hint={`Deductions used: ${inr(result.old_regime.deductions_used)}`}
                  testId="tax-old-card"
                />
                <RegimeCard
                  title="New Regime"
                  taxable={result.new_regime.taxable_income}
                  tax={result.new_regime.tax}
                  recommended={result.recommended_regime === "new"}
                  hint={`Std. deduction: ${inr(result.new_regime.deductions_used)}`}
                  testId="tax-new-card"
                />
              </div>

              <div className="tc-card p-6 bg-[#0a0a0a] text-white">
                <p className="tc-overline" style={{ color: "#d95d39" }}>The verdict</p>
                <h3 className="font-['Outfit'] text-2xl md:text-3xl font-bold mt-2 break-words">
                  Pick the {result.recommended_regime.toUpperCase()} regime — save {inr(result.savings)}
                </h3>
              </div>

              <div className="tc-card overflow-hidden">
                <div className="p-6 border-b border-[#e5e5e0]">
                  <p className="tc-overline">Advance tax schedule</p>
                  <h2 className="font-['Outfit'] text-2xl font-medium mt-1">Don't get penalized — pay on time</h2>
                </div>
                <table className="w-full text-sm min-w-[560px]" data-testid="tax-advance-schedule">
                  <thead className="bg-[#f4f1eb] text-[10px] tracking-[0.18em] uppercase text-[#5a5a53]">
                    <tr>
                      <th className="text-left px-6 py-3 font-bold">Due date</th>
                      <th className="text-left px-6 py-3 font-bold">Cumulative %</th>
                      <th className="text-right px-6 py-3 font-bold">Amount payable</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.advance_tax_schedule.map((s) => (
                      <tr key={s.due_date} className="border-t border-[#e5e5e0]">
                        <td className="px-6 py-3 font-medium">{s.due_date}</td>
                        <td className="px-6 py-3 text-[#5a5a53]">{s.percent}%</td>
                        <td className="px-6 py-3 text-right font-mono font-bold">{inr(s.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="tc-card p-12 text-center text-sm text-[#5a5a53]" data-testid="tax-placeholder">
              Punch in your numbers and hit "Compare regimes" to see the tax breakdown.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RegimeCard({ title, taxable, tax, recommended, hint, testId }) {
  return (
    <div className={`tc-card p-6 ${recommended ? "ring-2 ring-[#4a6b53] border-[#4a6b53]" : ""}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="tc-overline">{title}</p>
        {recommended && (
          <span className="text-[10px] font-bold tracking-wider uppercase text-[#4a6b53] bg-[#eef3ee] px-2 py-0.5 rounded-sm">
            Recommended
          </span>
        )}
      </div>
      <div className="mt-4">
        <div className="text-xs text-[#5a5a53]">Taxable income</div>
        <div className="font-mono text-lg font-medium">{inr(taxable)}</div>
      </div>
      <div className="mt-4 pt-4 border-t border-[#e5e5e0]">
        <div className="text-xs text-[#5a5a53]">Tax (incl. 4% cess)</div>
        <div className="font-['Outfit'] text-2xl md:text-3xl font-bold mt-1 break-words">{inr(tax)}</div>
      </div>
      <p className="text-[11px] text-[#a3a39b] mt-3">{hint}</p>
    </div>
  );
}
